import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TemplateDialog,
  type MessageTemplateRow,
} from '@/components/messaging/template-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

export const MessageTemplatesPanel = () => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const [editTarget, setEditTarget] = useState<MessageTemplateRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const list = useQuery({
    queryKey: ['messaging', 'templates'],
    queryFn: () => api.get<{ data: MessageTemplateRow[] }>('/messaging/templates?pageSize=100'),
  });

  const columns: DataTableColumn<MessageTemplateRow>[] = [
    {
      id: 'name',
      header: t('templates.columns.name'),
      cell: (row) => (
        <span className="inline-flex items-center gap-2">
          {row.name}
          {row.isSystem ? <Badge variant="neutral">{t('templates.system')}</Badge> : null}
        </span>
      ),
    },
    {
      id: 'preview',
      header: t('templates.columns.preview'),
      cell: (row) => {
        const text = row.bodySms || row.bodyPush || '';
        if (!text) return '—';
        return text.length > 80 ? `${text.slice(0, 80)}…` : text;
      },
      hideBelowMd: true,
    },
  ];

  return (
    <>
      <DataTable
        rows={list.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('templates.empty')}</p>}
        actionsHeader={tCommon('table.actions')}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditTarget(row);
              setEditOpen(true);
            }}
          >
            {row.isSystem ? t('templates.view') : tCommon('action.edit')}
          </Button>
        )}
      />

      <TemplateDialog
        open={editOpen}
        onOpenChange={(next) => {
          setEditOpen(next);
          if (!next) setEditTarget(null);
        }}
        template={editTarget}
      />
    </>
  );
};
